import { JSX } from 'solid-js/jsx-runtime';
import { Show, For, createSignal, onCleanup, createEffect, Setter, createMemo, Accessor } from 'solid-js';

import { Context, anyerror } from '@bitiotic/bitiotic';
import { Game, Persistent, Round, OnePlayersBets, PlayerBetMap, PlayAction, RoundWinnings } from '@bitiotic/numbet';

import { DebugObj } from './DebugObj';
import { DebugErr } from './DebugErr';
import { GamePlayerListener, StyledPNum } from './GamePlayerListener';
import { SectionHeader } from './SectionHeader';
import { currentPlayerGlowShadow, currentPlayerGlowRadius } from './PlayersList';
import { ReportRoundResults, ResultsSectionHeader } from './RoundResults';
import { BetMultiplier, BoxBorder, BoxStatus, formatCurrency, SpinButton } from './Widgets';

// Inverted PlayerBetMap, by spot, not by player
interface BetSpotMap {
    [betSpot: string]: {
        [player: string]: number,
    }
}

interface BetLimits {
    grant: number,
    winnings: number,
    betsPerRound: number,
}

// Return the number of bets placed and their total value
function currentBetSize(ctx: Context, myBets: OnePlayersBets): { betAmount: number, betCount: number } {
    let betAmount = 0;
    let betCount = 0;
    for (const [spot, betval] of Object.entries(myBets)) {
        ctx.assertGE(betval, 0, `bet $${betval} at ${spot} cannot be negative`);
        if (betval > 0) {
            betAmount += betval;
            betCount += 1;
        }
    }
    return { betAmount, betCount };
}


// Invert map "player->spot->number" into "spot->player->number" map
function invertBetMap(ctx: Context, playerBetMap: PlayerBetMap): BetSpotMap {
    const newMap: BetSpotMap = {};

    Object.entries(playerBetMap).forEach(([player, playerBets]) => {
        Object.entries(playerBets).forEach(([betSpotPath, bet]) => {
            const spotMap = newMap[betSpotPath] = (newMap[betSpotPath] || {});
            ctx.assertUndefined(spotMap[player],
                'Player', player, 'already in', spotMap);
            spotMap[player] = bet;
        });
    });
    return newMap;
}

function GuessInput(props: {
    ctx: Context,
    game: Game,
    round: Round,
    playerPath: Persistent.PlayerPath,
    getLocalGuess: Accessor<string | null>,
    setLocalGuess: Setter<string | null>,
    setRejection: Setter<unknown>,
    disabled: boolean,
}): JSX.Element {
    function sendGuess(ctx: Context, round: Round, guessStr?: string): Promise<void> {
        if (guessStr) {
            return round.setGuess(ctx, props.playerPath, guessStr)
                .catch(err => {
                    props.setRejection(err);
                }).then(() => {
                    props.setLocalGuess(guessStr);
                });
        }
        return Promise.resolve();
    }

    function dynamicClasses(disabled: boolean, currentGuess: string | null): { [clz: string]: boolean } {
        if (disabled) {
            return { 'border-grey-500': true };
        }
        if (currentGuess === null) {
            return { 'border-green-700': true };
        }
        return { 'border-green-500': true };
    }

    const getEmph = createMemo<BoxStatus>(() => {
        if (props.disabled) {
            return 'stale';
        }
        const lg = props.getLocalGuess();
        if (lg === null || lg === '') {
            return 'emph';
        }
        return 'de-emph';
    });

    const getId = createMemo<string>(() => {
        const p = props.game.getGamePath();
        return `user-guess-${p}-${props.round.getRoundLabel(props.ctx)}`;
    });

    const [getGuessUI, setGuessUI] = createSignal<string>(props.getLocalGuess() ?? '');

    createEffect(() => {
        // Be sure to update local signal when parent's guess is cleared out ...
        setGuessUI(props.getLocalGuess() ?? '');
    });

    return <BoxBorder status={getEmph()}>
        <div class="flex w-fill justify-between">
            <div class="p-2 inline-block">
                <input
                    id={getId()}
                    name={getId()}
                    autocomplete="off"
                    minlength="1"
                    disabled={props.disabled}
                    placeholder="Guess"
                    class={'appearance-none border w-16 sm:w-48 md:w-60 rounded py-1 sm:py-2 px-1 sm:px-3 text-black-700 leading-tight'
                        + 'focus:outline-none focus:shadow-outline invalid:border-red-600'}
                    classList={dynamicClasses(props.disabled, props.getLocalGuess())}
                    type="number"
                    step="any"
                    data-testid="input-guess"
                    value={getGuessUI()}
                    onInput={ev => { setGuessUI(ev.target.value); }}
                />
            </div>
            <div class="py-2 inline-block">
                <SpinButton
                    disabled={props.disabled}
                    onClick={() => sendGuess(props.ctx, props.round, getGuessUI())}
                    color='green'
                >Guess</SpinButton>
            </div>
        </div>
    </BoxBorder>;
}

function AnswerInput(props: {
    ctx: Context,
    game: Game,
    round: Round,
    playerPath: Persistent.PlayerPath,
    getAnswerStr: Accessor<string>,
    setAnswerStr: Setter<string>,
    setRejection: Setter<unknown>,
    disabled: boolean
}): JSX.Element {
    function sendAnswer(ctx: Context, game: Game, round: Round, answerStr?: string): Promise<boolean> {
        if (answerStr) {
            return round.resolveGuessesForAnswer(ctx, answerStr, game)
                .then(() => {
                    ctx.log(`Server accepted answer "${answerStr}".  Closing round.`);
                    return props.game.closeRound(ctx, round)
                        .then(() => true);
                }).catch(err => {
                    ctx.log('Error on resolving guesses in sendAnswer:', err);
                    props.setRejection(err);
                    return false;
                });
        }
        return Promise.resolve(false);
    }

    const getEmph = createMemo<BoxStatus>(() => {
        if (props.disabled) {
            return 'stale';
        }
        return 'emph';
    });

    const getId = createMemo<string>(() => {
        const p = props.game.getGamePath();
        return `user-answer-${p}-${props.round.getRoundLabel(props.ctx)}`;
    });

    return <BoxBorder status={getEmph()}>
        <div class="flex w-fill justify-between">
            <div class="p-2 inline-block">
                <input
                    id={getId()}
                    name={getId()}
                    autocomplete={getId()}
                    minlength="1"
                    disabled={props.disabled}
                    placeholder="Answer"
                    class={'appearance-none border rounded w-16 sm:w-48 md:w-60 py-1 sm:py-2 px-1 sm:px-3 text-gray-700'
                        + 'leading-tight focus:outline-none focus:shadow-outline invalid:border-red-600'}
                    type="number"
                    step="any"
                    data-testid="input-answer"
                    value={props.getAnswerStr()}
                    onInput={ev => { props.setAnswerStr(ev.target.value); }}
                />
            </div>
            <div class="py-2 inline-block">
                <SpinButton
                    disabled={props.disabled}
                    onClick={() => {
                        return sendAnswer(props.ctx, props.game, props.round, props.getAnswerStr())
                            .then(sent => {
                                if (sent) {
                                    props.ctx.log('Answer sent, disconnecting');
                                    // Disconnect to prevent multiple answers (?)
                                    //answerInput = undefined;
                                } else {
                                    props.ctx.log('Answer did not send ... leaving as-is');
                                }
                            });
                    }}
                    color='green'
                >Answer</SpinButton>
            </div>
        </div>
    </BoxBorder>;
}

function ChipBetButton(props: {
    ctx: Context,
    answerIsPresent: boolean, // fully inactive
    buttonIsActive: boolean, // should be clickable (not above/below limits)
    //onClick: JSX.EventHandlerUnion<HTMLButtonElement, MouseEvent>
    onClick: () => void,
    children: JSX.Element,
}): JSX.Element {
    const common = 'font-semibold py-1 md:px-2 px-1 border-2 mx-1 md:mx-2 rounded';
    const active = 'border-green-900 text-green-700 bg-transparent' +
        ' hover:border-transparent hover:bg-green-600 hover:text-green-100';
    const inact = 'text-stone-700 hover:text-stone border border-stone-500 bg-transparent';
    const redundant = 'border-stone-900 text-green-100 bg-green-600';

    function classes(): string {
        if (props.answerIsPresent) {
            return common + ' ' + redundant + ' opacity-20';
        } else if (!props.buttonIsActive) {
            return common + ' ' + inact;
        } else {
            return common + ' ' + active;
        }
    }

    const [getInPress, setInPress] = createSignal<boolean>(false);
    const [getInLongPress, setInLongPress] = createSignal<boolean>(false);
    const longPressDelayMS = 200;
    const longPressIntervalMS = 150;
    let startLongTimer: NodeJS.Timeout | null = null;
    let intervalTimer: NodeJS.Timeout | null = null;

    function clickstart(_ev: Event): void {
        setInPress(true);
        setInLongPress(false);
        startLongTimer = setTimeout(() => {
            setInLongPress(true);
            props.onClick();
            intervalTimer = setInterval(() => {
                props.onClick();
            }, longPressIntervalMS);
            startLongTimer = null;
        }, longPressDelayMS);
    }

    function clickend(ev: Event): void {
        if (getInPress() && !getInLongPress()) {
            props.onClick();
        }
        cleanup(ev);
    }

    function cleanup(_ev: Event): void {
        setInPress(false);
        setInLongPress(false);
        if (startLongTimer) clearTimeout(startLongTimer);
        if (intervalTimer) clearInterval(intervalTimer);
    }

    return <button
        class={classes()}
        disabled={props.answerIsPresent || !props.buttonIsActive}
        //onClick={props.onClick}
        onPointerDown={clickstart}
        onPointerUp={clickend}
        onPointerOut={cleanup}
    >{props.children}</button>;
}

export function RoundTitle(props: {
    ctx: Context,
    game: Game,
    round: Round | null,
    getLocalGuess: () => string | null,
    limits: Readonly<BetLimits> | null,
    myBets: Readonly<OnePlayersBets>,
    myWinnings: Readonly<RoundWinnings> | null,
    playAction: PlayAction,
}): JSX.Element {
    function roundMofN(ctx: Context, game: Game, round: Round | null): JSX.Element {
        if (!round) {
            return <span></span>;
        }

        const rNum = round.getRoundNumber() + 1;
        const roundCt = game.getOptions().roundsPerGame(ctx);

        return <span>
            Round #{rNum} of {roundCt}
        </span>;
    }

    const netwinnings = createMemo<number>(() => {
        return props.myWinnings?.net ?? 0;
    });

    const grantAvailable = createMemo<number>(() => {
        if (props.limits) {
            if (['bet-missing-chips'].includes(props.playAction)) {
                const grant = props.limits.grant;
                if (grant > 0) {
                    const { betAmount } = currentBetSize(props.ctx, props.myBets);
                    if (betAmount <= grant) {
                        return grant - betAmount;
                    }
                    return 0;
                }
            }
        }
        return -1; // No useful data
    });

    const bettableWinnings = createMemo<number>(() => {
        if (props.limits) {
            if (['bet-missing-chips'].includes(props.playAction)) {
                const limit = props.limits.grant + props.limits.winnings;
                if (limit > 0) {
                    const { betAmount } = currentBetSize(props.ctx, props.myBets);
                    if (betAmount <= props.limits.grant) {
                        // Haven't spent the grant yet, all of winnings are bettable
                        return props.limits.winnings;
                    }
                    // Bet more than the grant, bettable winnings is whatever is left
                    if (betAmount <= limit) {
                        return limit - betAmount;
                    }
                    return 0; // Nothing left to bet
                }
            }
        }
        return -1; // No useful data
    });

    return <SectionHeader ctx={props.ctx}>
        {roundMofN(props.ctx, props.game, props.round)}
        <Show when={props.getLocalGuess() !== null}>
            &nbsp;- guess {props.getLocalGuess()}
        </Show>
        <Show when={grantAvailable() > 0 && bettableWinnings() > 0}>
            &nbsp;- {formatCurrency(grantAvailable())} + {formatCurrency(bettableWinnings())} bettable
        </Show>
        <Show when={grantAvailable() === 0 && bettableWinnings() > 0}>
            &nbsp;- {formatCurrency(bettableWinnings())} bettable
        </Show>
        <Show when={grantAvailable() >= 0 && bettableWinnings() === 0}>
            &nbsp;- {formatCurrency(grantAvailable())} bettable
        </Show>
        <Show when={netwinnings() > 0}>
            &nbsp;- made {formatCurrency(netwinnings())}
        </Show>
        <Show when={(netwinnings() === 0) && (props.myWinnings)}>
            &nbsp;- made nothing
        </Show>
        <Show when={netwinnings() < 0}>
            &nbsp;- <span class="italic">lost {formatCurrency(0 - netwinnings())}</span>
        </Show>
    </SectionHeader>;
}

function WaitForGuesses(props: {
    ctx: Context,
    game: Game,
    visible: boolean,
    isManager: boolean,
    setRejection: Setter<unknown>,
    round: Round,
    disabled: boolean,
}): JSX.Element {
    function closeGuessing(ctx: Context, game: Game, round: Round): Promise<void> {
        return round.layoutGuesses(ctx.newCtx('layout'), game)
            .catch(err => {
                props.setRejection(err);
            })
            .then(_ignored => { });
    }

    return <Show when={props.visible}>
        <Show when={props.isManager}>
            <div class="p-3 w-fill flex justify-center">
                <SpinButton
                    disabled={props.disabled}
                    onClick={() => closeGuessing(props.ctx, props.game, props.round)}
                    color="green"
                >End Guessing Phase</SpinButton>
            </div>
        </Show>
        <Show when={!props.isManager}>
            <div class="p-3">
                Waiting for Manager to close guessing ...
            </div>
        </Show>
    </Show>;
}


export function RoundBoard(props: {
    ctx: Context,
    game: Game,
    round: Round,
    playerPath: Persistent.PlayerPath,
    gamePlayerListener: GamePlayerListener,
}): JSX.Element {
    const players = props.game.getPlayerListCache(props.ctx); // immutable
    const playerPath = props.playerPath; // immutable
    const ctx = props.ctx.newCtx('RoundBoard'); // immutable
    const betSpots = props.round.getBetSpotPaths(ctx).sort(); // immutable
    const isManager: boolean = props.game.isManager(ctx, playerPath); // immutable
    const opts = props.game.getOptions();
    const limits = {
        betsPerRound: opts.betsPerRound(ctx), // immutable
        grant: opts.grantPerRound(ctx), // immutable
    };

    const [getReadyPlayers, setReadyPlayers] = createSignal<Readonly<Persistent.PlayerPath[]> | null>(null);
    const [getGuessPresence, setGuessPresence] = createSignal<Readonly<Persistent.PlayerPath[]> | null>(null);
    const [getLocalGuess, setLocalGuess] = createSignal<string | null>(null);
    const [getGuessLayout, setGuessLayout] = createSignal<Readonly<Persistent.GuessLayout> | null>(null);
    const [getBetMap, setBetMap] = createSignal<PlayerBetMap>({});
    const [getSpotMap, setSpotMap] = createSignal<Readonly<BetSpotMap>>({});
    const [getResults, setResults] = createSignal<Readonly<Persistent.RoundResults> | null>(null);
    const [getPlayAction, setPlayAction] = createSignal<PlayAction>('unknown');
    const [getPlayerRoundWinnings, setPlayerRoundWinnings] = createSignal<Readonly<RoundWinnings> | null>(null);
    const [getRejection, setRejection] = createSignal<null | unknown>(null);
    const [getAnswerStr, setAnswerStr] = createSignal<string>('');
    const [getBetLimits, setBetLimits] = createSignal<null | Readonly<BetLimits>>(null);
    const [getGameCompleted, setGameCompleted] = createSignal<boolean>(props.game.isCompleted());

    createMemo(() => {
        const round = props.round; // force a dependency on the Round
        props.game.getPlayerGameWinnings(ctx.newCtx('fetchwin'), playerPath).then(winnings => {
            // These limits should be stable for the whole round
            ctx.log('setBetLimits: Updating from round', round, 'winnings', winnings);
            setBetLimits({
                grant: limits.grant,
                winnings: winnings.winnings,
                betsPerRound: limits.betsPerRound,
            });
        });
    });

    createEffect(() => registerRoundListeners(ctx.newCtx('listeners'), props.round, playerPath));

    function registerRoundListeners(ctx: Context, round: Round, currentPlayerPath: Persistent.PlayerPath): void {
        setReadyPlayers(null);
        setGuessPresence(null);
        setLocalGuess(null);
        setAnswerStr('');
        setGuessLayout(null);
        setBetMap({});
        setSpotMap({});
        setResults(null);
        setPlayerRoundWinnings(null);
        setPlayAction('unknown');

        const cleanReadyListen = round.listenForReadyPlayers(ctx,
            (ctx, ready) => {
                setReadyPlayers(Array.from(ready.keys()));
                updatePlayAction(ctx, round);
            },
            err => setRejection(err)
        );

        // This is only for the manager of the game to do.  Regular users just wait for the
        // guess layout to show up.
        const cleanPresenceListen =
            isManager ? round.listenForGuessPresence(ctx,
                currentPlayerPath,
                (ctx, present, _newPlayers, myGuess) => {
                    setGuessPresence(Array.from(present.keys()));
                    if (myGuess !== null) {
                        setLocalGuess(myGuess);
                    }
                    updatePlayAction(ctx, round);
                },
                err => setRejection(err)
            ) : () => { /*no cleanup*/ };

        const cleanLayoutListen = round.listenForGuessLayout(ctx,
            (ctx, layout) => {
                setGuessLayout(layout);
                updatePlayAction(ctx, round);
            },
            err => setRejection(err)
        );

        const cleanBetListen = round.listenForPlayerBets(ctx,
            (ctx, bets, _updated) => {
                // TODO: merge official bet info with "local" bet info?  (E.g., if have
                // bet in multiple places while waiting for update from first bet?)
                setBetMap({ ...bets }); // clone so SolidJS knows it changed
                setSpotMap(invertBetMap(ctx, bets));
                updatePlayAction(ctx, round);
            },
            err => setRejection(err)
        );

        const cleanResultListen = round.listenForRoundResults(ctx,
            (ctx, results) => {
                setResults({ ...results }); // clone for SolidJS
                if (results.answer === null) {
                    setAnswerStr('');
                } else {
                    setAnswerStr(`${results.answer}`);
                }
                updatePlayAction(ctx, round);
                /*async*/
                round.getPlayerRoundWinnings(ctx, playerPath)
                    .then(roundWinnings => {
                        setPlayerRoundWinnings(roundWinnings); // goes from null to non-null, don't need to clone
                        updatePlayAction(ctx, round);
                    })
                    .catch(err => {
                        setRejection(err);
                    });
            },
            err => {
                if (anyerror.causedByPermissionDenied(err)) {
                    ctx.log('RoundResults permission denied error, Ignoring:', err);
                    setRejection('Not allowed to see round details.');
                } else {
                    setRejection(err);
                }
            }
        );

        onCleanup(() => {
            ctx.log('RoundState cleanup for', round.instanceId, round.getRoundPath(ctx));
            cleanReadyListen();
            cleanLayoutListen();
            cleanResultListen();
            cleanPresenceListen();
            cleanBetListen();
        });
    }

    function registerGameListeners(ctx: Context, game: Game): void {
        const gameDoneUnsub = game.listenForGameCompleted(ctx, () => {
            setGameCompleted(true);
        },
            err => setRejection(err),
        );

        onCleanup(() => {
            gameDoneUnsub();
        });
    }

    createMemo(() => registerGameListeners(ctx.newCtx('game-listeners'), props.game));

    function updatePlayAction(ctx: Context, round: Round): void {
        // Expect the Round instance to have been updated by the listen callbacks.
        const action = round.getPlayerAction(ctx, playerPath);

        if (action === 'declare-ready') {
            // TODO: Supress this change if its already in flight, somehow?
            declareReady(ctx, round);
        } else {
            ctx.log('set playAction to', action, 'on round', round.getRoundPath(ctx));
            setPlayAction(action);
        }
    }

    // Check status after sending a guess
    createEffect(() => {
        getLocalGuess(); // ignore value, just listen for changes
        updatePlayAction(props.ctx, props.round);
    });

    function declareReady(ctx: Context, round: Round | null): void {
        ctx.assertNotNull(round, 'round must not be null');

        /*async*/
        ctx.log('delcaring I am ready to play round');
        round.setPlayerIsReady(ctx, playerPath)
            .catch(err => {
                setRejection(err);
            });

        // XXX oustanding set-ready promise
    }

    function myBets(_ctx: Context): OnePlayersBets {
        const m = getBetMap();
        const bets = m[playerPath];
        if (bets) {
            return bets;
        }
        m[playerPath] = {};
        return m[playerPath];
    }

    ctx.log('RoundBoard for', props.round.instanceId, props.round.getRoundNumber(), props.round.getRoundPath(ctx));
    setRejection(null);

    return <div>
        <RoundTitle
            ctx={props.ctx.newCtx('RoundTitle')}
            game={props.game}
            round={props.round}
            limits={getBetLimits()}
            myBets={myBets(props.ctx)}
            getLocalGuess={getLocalGuess}
            myWinnings={getPlayerRoundWinnings()}
            playAction={getPlayAction()}
        />

        <DebugErr label="Round Rejection" err={getRejection()} />

        <Show when={getPlayAction() === 'unknown'}>
            Waiting for Round to load ...
        </Show>

        <div class="round-actions p-3 max-w-screen-sm mx-auto">
            {/* Step 1: Send "i'm ready" signal to Firebase (implictly done) */}

            {/* Step 2: Wait for question (out of band) and submit guess */}
            <GuessInput
                ctx={ctx.newCtx('GuessInput')}
                game={props.game}
                round={props.round}
                playerPath={playerPath}
                getLocalGuess={getLocalGuess}
                setLocalGuess={setLocalGuess}
                setRejection={setRejection}
                disabled={getGameCompleted() || (getPlayAction() !== 'guess' && getPlayAction() !== 'wait-for-guess-layout')} />

            {/* Step 2b: When done waiting for guesses, manager closes guessing. */}
            <WaitForGuesses
                ctx={ctx.newCtx('WaitForGuess')}
                game={props.game}
                visible={['wait-for-guess-layout', 'guess'].includes(getPlayAction())}
                isManager={isManager}
                round={props.round}
                setRejection={setRejection}
                disabled={getGameCompleted()}
            />

            {/* Step 3: Show the table of guesses and betting spots */}
            <Show when={['close-round', 'bet-missing-chips'].includes(getPlayAction())}>
                <div class="w-fill my-4">
                    <table class="table-fixed w-fill mx-auto" data-testid="RoundBoard">
                        <tbody>
                            <tr class="font-lg">
                                <th class="w-12">Guess</th>
                                <th class="w-8">By</th>
                                <th class="w-8">{/*separator column*/}</th>
                                <th class="">Bets</th>
                                <th class="w-12">&times;</th>
                            </tr>
                            <For each={betSpots}>
                                {/* Step4: Capture betting chips on the layed out guesses */}
                                {spot =>
                                    <TableRowTwoHalfs
                                        ctx={ctx.newCtx(`row-${spot}-top`)}
                                        spot={spot}
                                        round={props.round}
                                        playerPath={props.playerPath}
                                        layout={getGuessLayout()}
                                        allPlayerBets={getSpotMap()}
                                        betLimits={getBetLimits()}
                                        myBets={myBets(props.ctx)}
                                        results={getResults()}
                                        setRejection={setRejection}
                                        gameCompleted={getGameCompleted()}
                                        gamePlayerListener={props.gamePlayerListener}
                                    />
                                }
                            </For>
                        </tbody>
                    </table>
                </div>
            </Show>

            {/* Step 4b: Implicitly close betting when manager provides the answer */}
            <Show when={isManager}>
                <AnswerInput
                    ctx={ctx.newCtx('AnsInput')}
                    game={props.game}
                    round={props.round}
                    playerPath={props.playerPath}
                    setRejection={setRejection}
                    setAnswerStr={setAnswerStr}
                    getAnswerStr={getAnswerStr}
                    disabled={getGameCompleted() || (getPlayAction() !== 'bet-missing-chips')} />
            </Show>

            {/* Step 5: Show the result of the round (on the layout). */
            }        </div>

        <SectionHeader ctx={ctx}>
            <ResultsSectionHeader
                ctx={ctx}
                playerPath={props.playerPath}
                results={getResults()}
                myWinnings={getPlayerRoundWinnings()}
                gamePlayerListener={props.gamePlayerListener}
            />
        </SectionHeader>

        <Show when={getPlayAction() === 'close-round'}>
            <ReportRoundResults
                ctx={ctx.newCtx('roundResult')}
                myWinnings={getPlayerRoundWinnings()}
                playerPath={props.playerPath}
                results={getResults()}
                gamePlayerListener={props.gamePlayerListener}
            />
        </Show>

        <DebugObj
            _closed={true}
            _visible={false}
            action={getPlayAction()}
            ready={getReadyPlayers()}
            guessPresent={getGuessPresence()}
            layout={getGuessLayout()}
            bets={getBetMap()}
            spotMap={getSpotMap()}
            game={props.game.getGamePath()}
            players={players}
            isMgr={isManager}
            round={props.round.getRoundPath(ctx)}
            roundNum={props.round.getRoundNumber()}
        />

    </div >;
}

function TableRowTwoHalfs(props: {
    ctx: Context,
    round: Round,
    playerPath: Persistent.PlayerPath,
    spot: Persistent.BetSpotPath,
    layout: Readonly<Persistent.GuessLayout> | null,
    allPlayerBets: Readonly<BetSpotMap>,
    betLimits: Readonly<BetLimits> | null,
    myBets: OnePlayersBets, // Read-write, but gets overwritten on updates from server
    setRejection: Setter<unknown>,
    results: Readonly<Persistent.RoundResults> | null,
    gameCompleted: boolean,
    gamePlayerListener: GamePlayerListener,
}): JSX.Element {
    function getValStr(spot: Persistent.BetSpotPath): string {
        if (spot === Persistent.BetSpot_Underflow) {
            return '<';
        }

        const guesses: Persistent.Guesses = (props.layout?.[props.spot]) || {};
        const player0 = Object.keys(guesses)[0];
        return (player0 && guesses[player0]?.rawGuess) || '';
    }

    function rowStyle(
        spot: Persistent.BetSpotPath,
        valStr: string,
        results: Readonly<Persistent.RoundResults> | null): Record<string, string> {

        const fgColor = 'pink';
        let bgColor = 'transparent';
        let boxShadow = '0px 0px 0px 0xp #000';

        if (results?.answer) {
            if (results.winningSpotPath === spot) {
                // winning row
                bgColor = '#5f4';
                // boxShadow: xOffset yOffset blurSize spreadSize color
                boxShadow = '0px 0px 8px 4px ' + bgColor;
            } else {
                // losing row
                bgColor = 'transparent';
            }
        }

        const result: Record<string, string> = {
            'text-color': fgColor,
            'background-color': bgColor,
            'box-shadow': boxShadow,
        };

        // Set visiblity of row.   For Safari, need to set 'display: none' on hidden rows too.
        // https://stackoverflow.com/questions/69779526/css-visibilitycollapse-bug-between-safari-and-chrome

        if ((spot === Persistent.BetSpot_Underflow) || (valStr !== '')) {
            result['visibility'] = 'visible';
        } else {
            result['visibility'] = 'collapse';
            result['display'] = 'none';
        }

        return result;
    }

    function betAllowed(ctx: Context, oldVal: number, newVal: number): boolean {
        ctx.assertGE(oldVal, 0, 'old bet value cannot be negative');

        if (newVal < 0) { // common case
            return false;
        }

        if (oldVal === newVal) { // shouldn't happen
            return true;
        }

        if (props.betLimits === null) {
            return false; // Nothing allowed until limits are known
        }

        // Sum up current betting state
        const { betAmount, betCount } = currentBetSize(ctx, props.myBets);

        // If starting a bet on a new spot, user must not have used all their bet spots already
        if ((oldVal === 0) && (newVal > 0)) {
            if (betCount + 1 > props.betLimits.betsPerRound) {
                return false;
            }
        }

        // For increased bets, check that user has available $$ to bet
        if (newVal > oldVal) {
            const delta = newVal - oldVal;
            const grant = props.betLimits.grant;
            const winnings = props.betLimits.winnings;
            const limit = grant + winnings;
            const avail = limit - betAmount;
            if (delta > avail) {
                ctx.log('Disallow bet',
                    `($${delta} = $${oldVal} -> $${newVal})`,
                    `hitting $${limit} (= $${grant} + $${winnings}) with $${avail} available`);
                return false;
            }
        }

        // allowed
        return true;
    }

    function makeBet(ctx: Context, delta: 100 | -100): void {
        ctx.assertNotNull(props.round, 'round must not be null');

        const oldval = props.myBets[props.spot] ?? 0;
        const nval = oldval + delta;
        if (betAllowed(ctx, oldval, nval)) {
            props.myBets[props.spot] = nval;

            /* async */
            // XXX DEAL WITH RACE CONDITIONS?
            props.round.setBet(ctx, props.playerPath, props.myBets)
                .catch(err => {
                    props.setRejection(err);
                });
        }
    }

    function isLosingRow(_ctx: Context, results: Readonly<Persistent.RoundResults> | null,
        currentSpot: Persistent.BetSpotPath): boolean {
        if (results && results.answer) {
            const winningSpotPath = results.winningSpotPath;
            return (winningSpotPath !== currentSpot);
        }
        return false; // not a losing row until some row is the winner
    }

    function getAnswerStr(_ctx: Context, results: Readonly<Persistent.RoundResults> | null,
        currentSpot: Persistent.BetSpotPath): string {
        if (results && results.answer) {
            const winningSpotPath = results.winningSpotPath;
            if (winningSpotPath === currentSpot) {
                return `${results.answer}`;
            }
        }
        return '';
    }

    function spotMultiplier(spot: Persistent.BetSpotPath): number {
        const betSpot = props.round.getBetSpot(props.ctx, spot);
        return betSpot.payoutMultiplier;
    }

    function buttonIsActive(delta: 100 | -100): boolean {
        const oldval = props.myBets[props.spot] ?? 0;
        const nval = oldval + delta;
        return betAllowed(props.ctx, oldval, nval) && !props.gameCompleted;
    }

    /* <border> <guess> <guesser> <bet100> <bet200> <multiplier> */
    /* <border> <guess> <guesser> <bet+100> <bet-100> <multiplier> */
    /* <border> <answer> <skip> <other's bets> <skip> */
    return <>
        <tr style={rowStyle(props.spot, getValStr(props.spot), props.results)} data-testid="row-upper">
            <td classList={
                {
                    'text-center': true,
                    'font-bold': props.spot !== Persistent.BetSpot_Underflow,
                    'align-top': true,
                    'p-1': true,

                }
            } data-testid='cell-value'>
                {getValStr(props.spot)}
            </td>
            <td rowspan="2" class="align-top p-1" data-testid="cell-guessers">
                <BetSpotGuessers
                    ctx={props.ctx}
                    spot={props.spot}
                    round={props.round}
                    layout={props.layout}
                    currentPlayerPath={props.playerPath}
                    gamePlayerListener={props.gamePlayerListener}
                    gameCompleted={props.gameCompleted}
                />
            </td>
            <td rowspan="2" class="content-start">
                <div class="bg-gray-300 py-4 h-16 w-2 self-start">
                </div>
            </td>
            <td>
                {/* for making bets */}
                <div class="break-inside-avoid whitespace-nowrap" data-testid="cell-bets">
                    <ChipBetButton
                        ctx={props.ctx}
                        answerIsPresent={Boolean(props.results?.answer)}
                        buttonIsActive={buttonIsActive(-100)}
                        onClick={() => makeBet(props.ctx.newCtx('bet-100'), -100)}
                    >
                        <div class="px-1 lg:px-2">
                            <span class="hidden sm:inline">Bet&nbsp;</span>
                            {formatCurrency(-100)}
                        </div>
                    </ChipBetButton>
                    <ChipBetButton
                        ctx={props.ctx}
                        answerIsPresent={Boolean(props.results?.answer)}
                        buttonIsActive={buttonIsActive(100)}
                        onClick={() => makeBet(props.ctx.newCtx('bet+100'), 100)}
                    >
                        <div class="px-1 lg:px-2">
                            <span class="hidden sm:inline">Bet&nbsp;</span>
                            +{formatCurrency(100)}
                        </div>
                    </ChipBetButton>
                </div>
            </td>
            <td rowspan="2">
                <div class="text-center" data-testid="cell-multiplier">
                    <BetMultiplier val={spotMultiplier(props.spot)} />
                </div>
            </td>
        </tr >

        <tr style={rowStyle(props.spot, getValStr(props.spot), props.results)} data-testid="row-lower">
            <td class="text-center text-green-600 font-extrabold" data-testid="cell-answer">
                {getAnswerStr(props.ctx, props.results, props.spot)}
            </td>
            {/* Upper row "guesser" cell flows into this cell */}
            {/* Upper row "separator" cell flows into this cell */}
            <td>
                {/* made bets */}
                <BetSpotMadeBets
                    ctx={props.ctx}
                    spot={props.spot}
                    round={props.round}
                    layout={props.layout}
                    bets={props.allPlayerBets}
                    currentPlayerPath={props.playerPath}
                    losingRow={isLosingRow(props.ctx, props.results, props.spot)}
                    gamePlayerListener={props.gamePlayerListener}
                    gameCompleted={props.gameCompleted}
                />
            </td>
            {/* Upper row "multiplier" cell flows into this cell */}
        </tr >
    </>;
}

function BetSpotMadeBets(props: {
    ctx: Context,
    spot: Persistent.BetSpotPath,
    round: Round,
    layout: Readonly<Persistent.GuessLayout> | null,
    bets: Readonly<BetSpotMap>,
    currentPlayerPath: Persistent.PlayerPath,
    losingRow: boolean,
    gamePlayerListener: GamePlayerListener,
    gameCompleted: boolean,
}): JSX.Element {
    type OneBet = {
        player: Persistent.PlayerPath,
        playerName: string,
        color: string,
        label: string,
        value: number,
        textDecoration: 'line-through' | 'none',
    };

    const bets = createMemo<OneBet[]>(() => {
        const betObjs: OneBet[] = [];
        const playerBets = props.bets[props.spot];
        if (playerBets) {
            Object.entries(playerBets).forEach(([playerStr, betval]) => {
                const player = Persistent.asPlayerPath(playerStr);
                const nameGetter = props.gamePlayerListener.getNameSignal(props.ctx, player);
                const colorGetter = props.gamePlayerListener.getColorSignal(props.ctx, player);

                const deco: 'line-through' | 'none' = props.losingRow ? 'line-through' : 'none';
                const oneBet = {
                    player,
                    playerName: nameGetter(),
                    color: props.losingRow ? 'grey' : colorGetter(),
                    textDecoration: deco,
                };

                if (betval !== 0) {
                    betObjs.push({
                        ...oneBet,
                        value: betval,
                        label: formatCurrency(betval),
                    });
                }
            });
        }
        return betObjs.sort((a, b) => b.value - a.value);
    });

    const styleDisplay = 'inline-block';
    const styleMargin = '0.2em';

    return <div class="whitespace-nowrap" data-testid="cell-bettors">
        <Show when={bets().length === 0}>
            <div style={{
                display: styleDisplay,
                margin: styleMargin,
            }}>
                <div>&nbsp;</div>{' '}
                <div>&nbsp;</div>
            </div>
        </Show>
        <For each={bets()}>
            {bet => {
                const enableGlow = createMemo(() => {
                    return (props.currentPlayerPath === bet.player) && !props.gameCompleted;
                });

                return <div style={{
                    // color: bet.color,
                    display: styleDisplay,
                    padding: styleMargin,
                    'box-shadow': enableGlow() ? currentPlayerGlowShadow : undefined,
                    'border-radius': enableGlow() ? currentPlayerGlowRadius : undefined,
                    'text-decoration': bet.textDecoration,
                }}>
                    <div class="text-center" data-testid="subcell-singlebettor">
                        <div style={{
                            color: 'green',
                        }}>{bet.label}</div>{' '}
                        <StyledPNum
                            ctx={props.ctx}
                            playerPath={bet.player}
                            gamePlayerListener={props.gamePlayerListener}
                        />
                    </div>
                </div>;
            }}
        </For >
    </div>;
}

function BetSpotGuessers(props: {
    ctx: Context,
    round: Round,
    spot: Persistent.BetSpotPath,
    currentPlayerPath: Persistent.PlayerPath,
    layout: Readonly<Persistent.GuessLayout> | null,
    gamePlayerListener: GamePlayerListener,
    gameCompleted: boolean,
}): JSX.Element {
    const players = createMemo<Persistent.PlayerPath[]>(() => {
        const guessObjs: Persistent.PlayerPath[] = [];
        if (props.layout) {
            const rawGuesses = props.layout[props.spot];
            if (rawGuesses) {
                Object.entries(rawGuesses).forEach(([playerStr]) => {
                    const player = Persistent.asPlayerPath(playerStr);
                    guessObjs.push(player);
                });
            }
        }
        return guessObjs;
    });

    function divStyle(guesser: Persistent.PlayerPath): Record<string, string> {
        if ((props.currentPlayerPath === guesser) && !props.gameCompleted) {
            return {
                'box-shadow': currentPlayerGlowShadow,
                'border-radius': currentPlayerGlowRadius,
            };
        }
        return {};
    }

    return <For each={players()}>
        {playerPath => {
            return <div style={divStyle(playerPath)}>
                <StyledPNum
                    ctx={props.ctx}
                    playerPath={playerPath}
                    gamePlayerListener={props.gamePlayerListener} />
            </div>;
        }}
    </For>;
}

